import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { updateTrackingSettings } from '@/services/devices';
import { useDeviceStore } from '@/stores/useDeviceStore';
import { LOG_PREFIX } from '@/config/constants';
import { cn } from '@/lib/utils';
import { Crosshair, Scale } from 'lucide-react';
import type { Device, TrackingAccuracy, TrackingActivityType } from '@/types';

type TrackingProfile = 'precise' | 'balanced';

const PROFILES: Record<TrackingProfile, {
  accuracy: TrackingAccuracy | null;
  activityType: TrackingActivityType | null;
  distanceIntervalM: number | null;
}> = {
  precise: { accuracy: 'highest', activityType: 'fitness', distanceIntervalM: 0 },
  balanced: { accuracy: null, activityType: null, distanceIntervalM: null },
};

function detectCurrentProfile(device: Device): TrackingProfile {
  if (
    device.tracking_accuracy === 'highest' &&
    device.tracking_activity_type === 'fitness' &&
    device.tracking_distance_interval_m === 0
  ) {
    return 'precise';
  }
  return 'balanced';
}

interface TrackingSettingsFormProps {
  device: Device;
}

export function TrackingSettingsForm({ device }: TrackingSettingsFormProps) {
  const updateDevice = useDeviceStore((s) => s.updateDevice);
  const [selected, setSelected] = useState<TrackingProfile>(detectCurrentProfile(device));
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const hasChanges = selected !== detectCurrentProfile(device);

  async function handleSave() {
    setIsSaving(true);
    setSaved(false);
    try {
      const profile = PROFILES[selected];
      await updateTrackingSettings(device.id, {
        tracking_accuracy: profile.accuracy,
        tracking_activity_type: profile.activityType,
        tracking_distance_interval_m: profile.distanceIntervalM,
      });
      updateDevice(device.id, {
        tracking_accuracy: profile.accuracy,
        tracking_activity_type: profile.activityType,
        tracking_distance_interval_m: profile.distanceIntervalM,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(LOG_PREFIX, 'Failed to update tracking settings:', err);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Choose how this device tracks location. Changes take effect on the device's next app restart.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <button
          className={cn(
            'rounded-lg border-2 p-4 text-left transition-colors',
            selected === 'balanced'
              ? 'border-brand-purple bg-brand-purple-subtle'
              : 'border-gray-200 hover:border-gray-300'
          )}
          onClick={() => setSelected('balanced')}
        >
          <div className="mb-2 flex items-center gap-2">
            <Scale className="h-5 w-5 text-brand-purple" />
            <span className="text-sm font-semibold text-brand-black">Balanced</span>
          </div>
          <p className="text-xs text-gray-500">
            Standard tracking with optimized battery usage. Records location based on movement and distance traveled.
          </p>
        </button>

        <button
          className={cn(
            'rounded-lg border-2 p-4 text-left transition-colors',
            selected === 'precise'
              ? 'border-brand-purple bg-brand-purple-subtle'
              : 'border-gray-200 hover:border-gray-300'
          )}
          onClick={() => setSelected('precise')}
        >
          <div className="mb-2 flex items-center gap-2">
            <Crosshair className="h-5 w-5 text-brand-orange" />
            <span className="text-sm font-semibold text-brand-black">Precise</span>
          </div>
          <p className="text-xs text-gray-500">
            Captures every movement at the highest accuracy, updating each minute. Use when there is a concern about this device. Uses more battery.
          </p>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={isSaving || !hasChanges}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        {saved && <span className="text-xs text-green-600">Saved</span>}
      </div>
    </div>
  );
}
